// frontend/src/modules/payroll/api.ts
import { api, getToken } from '../../core/api-client';
import type {
  PayrollRun, CreatePayrollRunInput, PayrollComponent, AddComponentInput, Payslip,
  RecurringComponent, RecurringComponentInput, TaxRule, TaxRuleInput,
  PayrollSettings, PayslipConfig, PayslipYtd, AchPreview,
} from './types';

/**
 * Fetch a binary endpoint (PDF / CSV / ACH) as a blob WITH the Bearer auth
 * header, then trigger a browser download via a temporary object URL.
 *
 * Auth is header-only (no cookies), so a plain `<a href>` to these endpoints
 * opens an unauthenticated tab that renders a JSON 401 — always download
 * through this helper instead.
 */
export async function downloadFile(path: string, filename: string): Promise<void> {
  const token = getToken();
  const res = await fetch(path, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) {
    // Surface the backend's actionable message (e.g. which NACHA settings
    // are missing) instead of a bare status code.
    let message = `Download failed (${res.status})`;
    try {
      const body = await res.json();
      if (typeof body?.error?.message === 'string' && body.error.message) {
        message = body.error.message;
      }
    } catch {
      // Non-JSON error body — keep the generic message.
    }
    throw new Error(message);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Shape of GET /api/payroll/payslips/:id — enriched for self-view. */
export interface PayslipDetail {
  payslip: Payslip;
  run: PayrollRun;
  ytd?: PayslipYtd;
  components?: PayrollComponent[];
  employee_name?: string | null;
  employee?: {
    name: string;
    email: string | null;
    designation: string | null;
    employee_no: string | null;
  } | null;
}

export const payrollApi = {
  listRuns: () => api<{ runs: PayrollRun[] }>('/api/payroll/runs').then((r) => r.runs),
  getRun: (id: string) => api<{ run: PayrollRun }>(`/api/payroll/runs/${id}`).then((r) => r.run),
  createRun: (input: CreatePayrollRunInput) =>
    api<{ run: PayrollRun }>('/api/payroll/runs', { method: 'POST', body: input }).then((r) => r.run),
  computeRun: (id: string) =>
    api<{ employees_processed: number; components_inserted: number }>(`/api/payroll/runs/${id}/compute`, { method: 'POST', body: {} }),
  finalizeRun: (id: string) =>
    api<{ run: PayrollRun }>(`/api/payroll/runs/${id}/finalize`, { method: 'POST', body: {} }).then((r) => r.run),
  /**
   * Commit a FINALIZED run as paid. Irreversible: the backend freezes the run
   * against revert, cancel, and component edits from here on.
   */
  markRunPaid: (id: string) =>
    api<{ run: PayrollRun }>(`/api/payroll/runs/${id}/mark-paid`, { method: 'POST', body: {} }).then((r) => r.run),
  revertToDraft: (id: string) =>
    api<{ run: PayrollRun }>(`/api/payroll/runs/${id}/revert-to-draft`, { method: 'POST', body: {} }).then((r) => r.run),
  cancelRun: (id: string) =>
    api<{ run: PayrollRun }>(`/api/payroll/runs/${id}/cancel`, { method: 'POST', body: {} }).then((r) => r.run),
  distributeRun: (id: string) =>
    api<{ sent: number; failed: number; total: number }>(`/api/payroll/runs/${id}/distribute`, { method: 'POST', body: {} }),
  emailPayslip: (id: string) =>
    api<{ sent: boolean; reason?: string }>(`/api/payroll/payslips/${id}/email`, { method: 'POST', body: {} }),

  listComponents: (runId: string) =>
    api<{ rows: PayrollComponent[] }>(`/api/payroll/runs/${runId}/components`).then((r) => r.rows),
  listComponentsForEmployee: (runId: string, employeeId: string) =>
    api<{ rows: PayrollComponent[] }>(
      `/api/payroll/runs/${runId}/components?employee_id=${encodeURIComponent(employeeId)}`,
    ).then((r) => r.rows),
  addManualComponent: (runId: string, input: AddComponentInput) =>
    api<{ component: PayrollComponent }>(`/api/payroll/runs/${runId}/components`, { method: 'POST', body: input }).then((r) => r.component),
  removeComponent: (runId: string, componentId: string) =>
    api<{ ok: true }>(`/api/payroll/runs/${runId}/components/${componentId}`, { method: 'DELETE' }),

  listPayslipsForRun: (runId: string) =>
    api<{ payslips: Payslip[] }>(`/api/payroll/runs/${runId}/payslips`).then((r) => r.payslips),
  myPayslips: () =>
    api<{ payslips: Payslip[] }>('/api/payroll/me/payslips').then((r) => r.payslips),
  payslipPdfUrl: (id: string) => `/api/payroll/payslips/${id}/pdf`,
  /** Authed download of a payslip PDF (plain anchors would 401 — see downloadFile). */
  downloadPayslipPdf: (id: string) =>
    downloadFile(`/api/payroll/payslips/${id}/pdf`, `payslip-${id}.pdf`),

  runExportUrl: (runId: string) => `/api/payroll/runs/${runId}/export`,
  downloadRunCsv: (runId: string, periodStart: string) =>
    downloadFile(`/api/payroll/runs/${runId}/export`, `payroll-run-${String(periodStart).slice(0, 10)}.csv`),

  /** QuickBooks/Xero-ready journal-entry CSV for a FINALIZED run. */
  downloadRunJournal: (runId: string, periodStart: string) =>
    downloadFile(`/api/payroll/runs/${runId}/journal`, `payroll-journal-${String(periodStart).slice(0, 10)}.csv`),

  /**
   * The two payroll-report CSVs. The server aggregates and renders these —
   * they used to be built in the browser, which put them outside every
   * server-side gate (see api/modules/payroll/reports.service.ts).
   */
  downloadEmployeeCostCsv: () =>
    downloadFile('/api/payroll/reports/employees', 'payroll-employees-all-time.csv'),
  downloadRunSummaryCsv: () =>
    downloadFile('/api/payroll/reports/runs', 'payroll-runs-all-time.csv'),

  // ── Round-2 payroll depth ──────────────────────────────────────────────────

  listRecurringComponents: (employeeId?: string) =>
    api<{ rows: RecurringComponent[] }>(
      `/api/payroll/recurring-components${employeeId ? `?employee_id=${encodeURIComponent(employeeId)}` : ''}`,
    ).then((r) => r.rows),
  createRecurringComponent: (input: RecurringComponentInput) =>
    api<{ component: RecurringComponent }>('/api/payroll/recurring-components', { method: 'POST', body: input }).then((r) => r.component),
  updateRecurringComponent: (id: string, patch: Partial<RecurringComponentInput>) =>
    api<{ component: RecurringComponent }>(`/api/payroll/recurring-components/${id}`, { method: 'PATCH', body: patch }).then((r) => r.component),

  listTaxRules: () =>
    api<{ rules: TaxRule[] }>('/api/payroll/tax-rules').then((r) => r.rules),
  createTaxRule: (input: TaxRuleInput) =>
    api<{ rule: TaxRule }>('/api/payroll/tax-rules', { method: 'POST', body: input }).then((r) => r.rule),
  updateTaxRule: (id: string, patch: Partial<TaxRuleInput>) =>
    api<{ rule: TaxRule }>(`/api/payroll/tax-rules/${id}`, { method: 'PATCH', body: patch }).then((r) => r.rule),

  getSettings: () =>
    api<{ settings: PayrollSettings }>('/api/payroll/settings').then((r) => r.settings),
  updateSettings: (patch: Partial<Omit<PayrollSettings, 'payslip_config'>> & { payslip_config?: Partial<PayslipConfig> }) =>
    api<{ settings: PayrollSettings }>('/api/payroll/settings', { method: 'PUT', body: patch }).then((r) => r.settings),

  achPreview: (runId: string) =>
    api<AchPreview>(`/api/payroll/runs/${runId}/ach?preview=1`),
  downloadAch: (runId: string, periodStart: string) =>
    downloadFile(`/api/payroll/runs/${runId}/ach`, `payroll-ach-${String(periodStart).slice(0, 10)}.ach`),

  /**
   * Fetch a single payslip + its parent run directly (replaces the old
   * runs fan-out). Returns null on 404 / not-found.
   *
   * The response also carries `components` + `employee_name`/`employee` so the
   * detail page renders fully for employees who hold only payroll.view_self
   * (they cannot hit the payroll.view-gated components or HR employees routes).
   */
  async findPayslip(payslipId: string): Promise<PayslipDetail | null> {
    try {
      return await api<PayslipDetail>(`/api/payroll/payslips/${payslipId}`);
    } catch (err: any) {
      if (err?.status === 404) return null;
      throw err;
    }
  },

  /**
   * Pull every payslip across every accessible run. Used by the
   * cross-run payslips list + reports page. The backend has no
   * tenant-wide endpoint, so we fan out client-side.
   *
   * Per-run failures don't abort the whole listing; pass `onRunError` to
   * surface them (otherwise they're silently skipped, as before).
   */
  async listAllPayslips(
    onRunError?: (run: PayrollRun, err: unknown) => void,
  ): Promise<Array<Payslip & { run: PayrollRun }>> {
    const runs = await this.listRuns();
    const all: Array<Payslip & { run: PayrollRun }> = [];
    for (const run of runs) {
      try {
        const slips = await this.listPayslipsForRun(run.id);
        for (const s of slips) all.push({ ...s, run });
      } catch (err) {
        onRunError?.(run, err);
      }
    }
    return all;
  },
};
